import { useEffect, useMemo, useRef, useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Checkbox, FormControlLabel, IconButton, InputBase } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';

import { sortByLabel } from '@@common';

import { PrimitiveType } from '../../../../api/api';
import { themeColors } from '../../../../themes/colors';
import { ObjectPropertyType } from '../../Models/ObjectPropertyType';
import { IVariableScope } from '../../Models/VariableScopes/IVariableScope';
import { SchemaTypeVariableScope } from '../../Models/VariableScopes/SchemaTypeVariableScope';
import getPropertyTypeName from '../../Models/getPropertyTypeName';

const ID_SEPARATOR = '___';
const NEW_VARIABLE_PREFIX = '___NEW___';
const NEW_DICTIONARY_KEY_PREFIX = '___KEY___';

function resolvePathType(variableScope: IVariableScope, path: string[]): ObjectPropertyType | undefined {
	let scope = variableScope;
	let property: ObjectPropertyType | undefined;
	for (const segment of path) {
		property = scope.getVariable(segment);
		if (!property) {
			return undefined;
		}
		if (property.referenceEntity) {
			scope = new SchemaTypeVariableScope(property.referenceEntity);
		}
	}
	return property;
}

function getDictionaryKeyPaths(variableScope: IVariableScope, path: string[]): string[][] {
	let scope = variableScope;
	const keyPaths: string[][] = [];
	for (let index = 0; index < path.length; index++) {
		const segment = path[index];
		if (scope instanceof SchemaTypeVariableScope && scope.additionalProperties && !scope.getVariables().has(segment)) {
			keyPaths.push(path.slice(0, index + 1));
		}
		const property = scope.getVariable(segment);
		if (!property) {
			break;
		}
		if (property.referenceEntity) {
			scope = new SchemaTypeVariableScope(property.referenceEntity);
		}
	}
	return keyPaths;
}

type ObjectTreeViewItem = {
	id: string;
	label: string;
	disabled: boolean;
	isNew?: boolean;
	labelText?: string;
	name?: string;
	typeName?: string;
	declaration?: string;
	children?: ObjectTreeViewItem[];
};

function mapVariableScopeToTreeView(
	parentName: string | undefined,
	variableScope: IVariableScope,
	matchingPrimitiveTypes: PrimitiveType[] | undefined,
	canCreateVariable?: boolean,
	hasNewVariables?: boolean,
	requireCollection?: boolean,
): ObjectTreeViewItem[] {
	const items: ObjectTreeViewItem[] = Array.from(variableScope.getVariables().values()).map((property: ObjectPropertyType) => {
		const id = parentName ? `${parentName}${ID_SEPARATOR}${property.name}` : property.name;
		const typeString = getPropertyTypeName(property);

		return {
			id: id,
			label: `${property.name}: ${typeString}`,
			name: property.name,
			typeName: property.possiblyUnavailable ? `${typeString} (may be unavailable)` : typeString,
			declaration: property.scopeOrigins?.length ? `Declared by ${property.scopeOrigins.join(', ')}` : property.description,
			disabled:
				(requireCollection === true && !property.isMultiple) ||
				(property.type
					? matchingPrimitiveTypes !== undefined &&
						!(matchingPrimitiveTypes.includes(property.type) || (property.isMultiple && matchingPrimitiveTypes.includes(PrimitiveType.Collection)))
					: false),
			isNew: false,
			children:
				property.referenceEntity && !property.isMultiple // We can only expand into reference entities that are not multiple (arrays cannot be expanded and will need an expression to reference the item at an index)
					? mapVariableScopeToTreeView(
							id,
							new SchemaTypeVariableScope(property.referenceEntity),
							matchingPrimitiveTypes,
							canCreateVariable,
							hasNewVariables,
							requireCollection,
						)
					: [],
		};
	});

	// Sort items by label alphabetically
	items.sort(sortByLabel);

	// Add "+ Add variable" button at this level if canCreateVariable is enabled and no new variables exist
	if (canCreateVariable && !hasNewVariables) {
		const addButtonId = parentName ? `${NEW_VARIABLE_PREFIX}${parentName}` : NEW_VARIABLE_PREFIX;
		items.push({
			id: addButtonId,
			label: '+ Add variable',
			disabled: false,
			isNew: true,
			children: [],
		});
	}
	if (variableScope instanceof SchemaTypeVariableScope && variableScope.additionalProperties) {
		items.push({
			id: `${NEW_DICTIONARY_KEY_PREFIX}${parentName ?? ''}`,
			label: '+ Enter key',
			disabled: false,
			isNew: true,
			children: [],
		});
	}

	return items;
}

type VarPathSelectTreeProps = {
	matchingPrimitiveTypes?: PrimitiveType[];
	value: string[] | undefined;
	onValueChange?: (value: string[]) => void;
	variableScope: IVariableScope;
	onSelectionComplete?: () => void; // Called when a valid selection is made
	sx?: object;
	canCreateVariable?: boolean;
	requireCollection?: boolean;
};

export default function VarPathSelectTree({
	matchingPrimitiveTypes,
	value,
	onValueChange,
	variableScope,
	onSelectionComplete,
	sx = {},
	canCreateVariable,
	requireCollection,
}: VarPathSelectTreeProps) {
	const [newVariables, setNewVariables] = useState<string[][]>([]);
	const [allowAnyType, setAllowAnyType] = useState(false);
	const [inlineEditingId, setInlineEditingId] = useState<string | null>(null);
	const [inlineEditValue, setInlineEditValue] = useState<string>('');
	const inlineEditConfirmedRef = useRef(false);
	const [inlineAddingId, setInlineAddingId] = useState<string | null>(null);
	const [inlineAddValue, setInlineAddValue] = useState<string>('');
	const inlineAddConfirmedRef = useRef(false);

	const handleEditVariable = (itemId: string) => {
		const pathArray = itemId.split(ID_SEPARATOR);
		setInlineEditingId(itemId);
		setInlineEditValue(pathArray[pathArray.length - 1]);
		inlineEditConfirmedRef.current = false;
	};

	const handleInlineEditConfirm = (itemId: string) => {
		if (inlineEditConfirmedRef.current) return;
		inlineEditConfirmedRef.current = true;
		const trimmed = inlineEditValue.trim();
		if (trimmed) {
			const pathArray = itemId.split(ID_SEPARATOR);
			const newPath = pathArray.length > 1 ? [...pathArray.slice(0, -1), trimmed] : [trimmed];
			setNewVariables((prev) => {
				const updated = prev.filter((p) => p.join(ID_SEPARATOR) !== itemId);
				return [...updated, newPath];
			});
			if (value && value.join(ID_SEPARATOR) === itemId) {
				onValueChange?.(newPath);
			}
		}
		setInlineEditingId(null);
	};

	const handleInlineEditKeyDown = (e: React.KeyboardEvent, itemId: string) => {
		e.stopPropagation();

		if (e.key === 'Enter') {
			e.preventDefault();
			handleInlineEditConfirm(itemId);
		} else if (e.key === 'Escape') {
			setInlineEditingId(null);
		}
	};

	const handleInlineAddConfirm = (addButtonId: string) => {
		if (inlineAddConfirmedRef.current) return;
		inlineAddConfirmedRef.current = true;
		const trimmed = inlineAddValue.trim();
		if (trimmed) {
			const prefix = addButtonId.startsWith(NEW_DICTIONARY_KEY_PREFIX) ? NEW_DICTIONARY_KEY_PREFIX : NEW_VARIABLE_PREFIX;
			const parentPath = addButtonId.substring(prefix.length);
			const newPath = parentPath ? [...parentPath.split(ID_SEPARATOR), trimmed] : [trimmed];
			setNewVariables((prev) => [...prev, newPath]);
			onValueChange?.(newPath);
			onSelectionComplete?.();
		}
		setInlineAddingId(null);
	};

	const handleInlineAddKeyDown = (e: React.KeyboardEvent, addButtonId: string) => {
		e.stopPropagation();

		if (e.key === 'Enter') {
			e.preventDefault();
			handleInlineAddConfirm(addButtonId);
		} else if (e.key === 'Escape') {
			setInlineAddingId(null);
		}
	};

	const items: ObjectTreeViewItem[] = useMemo<ObjectTreeViewItem[]>(() => {
		const hasNewVariables = newVariables.length > 0;
		const effectiveMatchingTypes = allowAnyType ? undefined : matchingPrimitiveTypes;
		const baseItems = mapVariableScopeToTreeView(undefined, variableScope, effectiveMatchingTypes, canCreateVariable, hasNewVariables, requireCollection);

		// Add any new variables that don't exist in the base items
		const newItems = [...baseItems];

		newVariables.forEach((varPath) => {
			// Check if this path already exists in the tree
			const varId = varPath.join(ID_SEPARATOR);
			const existsInTree = (items: ObjectTreeViewItem[]): boolean => {
				for (const item of items) {
					if (item.id === varId) return true;
					if (item.children && existsInTree(item.children)) return true;
				}
				return false;
			};

			if (!existsInTree(baseItems)) {
				// Add the new variable to the tree
				const propertyType = resolvePathType(variableScope, varPath);
				const labelText = propertyType
					? `${varPath[varPath.length - 1]}: ${getPropertyTypeName(propertyType)}`
					: `${varPath[varPath.length - 1]} (new)`;
				const newItem: ObjectTreeViewItem = {
					id: varId,
					label: labelText,
					labelText: labelText,
					name: propertyType ? varPath[varPath.length - 1] : undefined,
					typeName: propertyType ? getPropertyTypeName(propertyType) : undefined,
					disabled: !!propertyType && (
						(requireCollection === true && !propertyType.isMultiple) ||
						(effectiveMatchingTypes !== undefined &&
							!effectiveMatchingTypes.includes(propertyType.type) &&
							!(propertyType.isMultiple && effectiveMatchingTypes.includes(PrimitiveType.Collection)))
					),
					isNew: true,
					children: propertyType?.referenceEntity
						? mapVariableScopeToTreeView(varId, new SchemaTypeVariableScope(propertyType.referenceEntity), effectiveMatchingTypes, canCreateVariable, hasNewVariables, requireCollection)
						: [],
				};

				if (varPath.length > 1) {
					// Nested variable — insert under its parent node rather than at root level.
					const parentId = varPath.slice(0, -1).join(ID_SEPARATOR);
					const insertIntoParent = (items: ObjectTreeViewItem[]): boolean => {
						for (const item of items) {
							if (item.id === parentId) {
								item.children = [...(item.children ?? []), newItem];
								return true;
							}
							if (item.children && insertIntoParent(item.children)) return true;
						}
						return false;
					};
					if (!insertIntoParent(newItems)) {
						// Parent not found in tree — fall back to root so the item is at least visible.
						newItems.push(newItem);
					}
				} else {
					newItems.push(newItem);
				}
			}
		});

		return newItems;
	}, [matchingPrimitiveTypes, variableScope, canCreateVariable, newVariables, allowAnyType, requireCollection]);

	// Check if the current value points to a non-existing variable
	useEffect(() => {
		if (!value || value.length === 0) return;
		const dictionaryKeyPaths = getDictionaryKeyPaths(variableScope, value);
		if (!canCreateVariable && dictionaryKeyPaths.length === 0) return;

		const varId = value.join(ID_SEPARATOR);
		const existsInTree = (items: ObjectTreeViewItem[]): boolean => {
			for (const item of items) {
				if (item.id === varId && !item.isNew) return true;
				if (item.children && existsInTree(item.children)) return true;
			}
			return false;
		};

		if (!existsInTree(items)) {
			const pathsToAdd = dictionaryKeyPaths.length > 0 ? dictionaryKeyPaths : [value];
			setNewVariables((prev) => {
				const additions = pathsToAdd.filter((path) => !prev.some((existing) => existing.join(ID_SEPARATOR) === path.join(ID_SEPARATOR)));
				return additions.length > 0 ? [...prev, ...additions] : prev;
			});
		}
	}, [value, items, canCreateVariable, variableScope]);

	// Get the parent path IDs for a selected item (to expand the tree to show the selection)
	const getParentPaths = (itemId: string): string[] => {
		const parts = itemId.split(ID_SEPARATOR);
		const paths: string[] = [];

		for (let i = 1; i < parts.length; i++) {
			paths.push(parts.slice(0, i).join(ID_SEPARATOR));
		}

		return paths;
	};

	const [expandedItems, setExpandedItems] = useState<string[]>([]);
	const [userExpandedItems, setUserExpandedItems] = useState<string[]>([]);

	// Current selected item based on props
	const currentSelectedItem = useMemo(() => {
		if (!value || value.length === 0) return null;

		return value.join(ID_SEPARATOR);
	}, [value]);

	// Set up expansion based on selected item - combine user expansions with required parent paths
	useEffect(() => {
		if (currentSelectedItem) {
			const parentPaths = getParentPaths(currentSelectedItem);
			// Merge parent paths with user-expanded items
			const combined = new Set([...userExpandedItems, ...parentPaths]);
			const newExpanded = Array.from(combined);
			setExpandedItems(newExpanded);
		} else {
			// No selection, just use user-expanded items
			setExpandedItems(userExpandedItems);
		}
	}, [currentSelectedItem, userExpandedItems]);

	// Handle user expand/collapse actions
	const handleExpandedItemsChange = (event: React.SyntheticEvent, itemIds: string[]) => {
		setUserExpandedItems(itemIds);
	};

	const handleSelectionChange = (event: React.SyntheticEvent, itemId: string | null) => {
		if (itemId) {
			// Check if this is the "Add variable" button
			if (itemId.startsWith(NEW_VARIABLE_PREFIX) || itemId.startsWith(NEW_DICTIONARY_KEY_PREFIX)) {
				setInlineAddingId(itemId);
				setInlineAddValue('');
				inlineAddConfirmedRef.current = false;
				return;
			}

			// Check if the item is disabled (but allow selection if allowAnyType is true)
			const selectedItem = findItemInTree(items, itemId);
			if (selectedItem && selectedItem.disabled && !allowAnyType) {
				// Don't allow selection of disabled items unless allowAnyType is enabled
				return;
			}

			const pathArray = itemId.split(ID_SEPARATOR);

			// Check if this is a new variable being selected
			const isNewVariable = newVariables.some((v) => v.join(ID_SEPARATOR) === itemId);

			// If selecting an existing (non-new) variable, clear all new variables
			if (!isNewVariable) {
				setNewVariables([]);
			} else {
				// Activate inline editing when clicking a new variable
				handleEditVariable(itemId);
			}

			onValueChange?.(pathArray);
			// Notify parent that selection is complete
			onSelectionComplete?.();
		}
	};

	const handleDeleteVariable = (itemId: string) => {
		setNewVariables((prev) => prev.filter((p) => p.join(ID_SEPARATOR) !== itemId));

		// If the deleted variable was selected, clear the selection
		if (value && value.join(ID_SEPARATOR) === itemId) {
			onValueChange?.([]);
		}
	};

	// Recursive helper to find item in tree
	const findItemInTree = (items: ObjectTreeViewItem[], itemId: string): ObjectTreeViewItem | undefined => {
		for (const item of items) {
			if (item.id === itemId) return item;
			if (item.children) {
				const found = findItemInTree(item.children, itemId);
				if (found) return found;
			}
		}
		return undefined;
	};

	// Render tree items recursively
	const renderTreeItems = (items: ObjectTreeViewItem[]): JSX.Element[] => {
		return items.map((item) => {
			const itemId = (item as any).id;
			const isAddButton = itemId.startsWith(NEW_VARIABLE_PREFIX) || itemId.startsWith(NEW_DICTIONARY_KEY_PREFIX);
			const isNew = item.isNew && !isAddButton;
			const hasChildren = item.children && item.children.length > 0;

			const label = isNew ? (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					{inlineEditingId === itemId ? (
						<>
							<InputBase
								autoFocus
								value={inlineEditValue}
								onChange={(e) => setInlineEditValue(e.target.value)}
								onBlur={() => handleInlineEditConfirm(itemId)}
								onKeyDown={(e) => handleInlineEditKeyDown(e, itemId)}
								onClick={(e) => e.stopPropagation()}
								sx={{ fontSize: 'inherit', border: '1px solid', borderColor: 'primary.main', borderRadius: 0.5, px: 0.5, minWidth: 80 }}
							/>
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									handleInlineEditConfirm(itemId);
								}}
								sx={{ padding: '2px' }}
								title="Confirm"
							>
								<CheckIcon sx={{ fontSize: '14px' }} />
							</IconButton>
						</>
					) : (
						<span style={{ color: themeColors.primary.main }}>{item.labelText || item.label}</span>
					)}
					{inlineEditingId !== itemId && (
						<>
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									handleEditVariable(itemId);
								}}
								sx={{ padding: '2px' }}
							>
								<EditIcon sx={{ fontSize: '14px' }} />
							</IconButton>
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									handleDeleteVariable(itemId);
								}}
								sx={{ padding: '2px' }}
								title="Remove variable"
							>
								<DeleteIcon sx={{ fontSize: '14px' }} />
							</IconButton>
						</>
					)}
				</Box>
			) : isAddButton && inlineAddingId === itemId ? (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					<InputBase
							autoFocus
							placeholder={itemId.startsWith(NEW_DICTIONARY_KEY_PREFIX) ? 'Key name…' : 'Variable name…'}
						value={inlineAddValue}
						onChange={(e) => setInlineAddValue(e.target.value)}
						onBlur={() => handleInlineAddConfirm(itemId)}
						onKeyDown={(e) => handleInlineAddKeyDown(e, itemId)}
						onClick={(e) => e.stopPropagation()}
						sx={{ fontSize: 'inherit', border: '1px solid', borderColor: 'primary.main', borderRadius: 0.5, px: 0.5, minWidth: 100 }}
					/>
					<IconButton
						size="small"
						onClick={(e) => {
							e.stopPropagation();
							handleInlineAddConfirm(itemId);
						}}
						sx={{ padding: '2px' }}
						title="Confirm"
					>
						<CheckIcon sx={{ fontSize: '14px' }} />
					</IconButton>
				</Box>
			) : item.name && item.typeName ? (
				<Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
					<Box component="span" sx={{ fontWeight: 500 }}>{item.name}</Box>
					<Box
						component="span"
						sx={{
							color: 'text.secondary',
							fontSize: '0.875em',
							'&::before': { content: '"·"', mr: 0.75 },
						}}
					>
						{item.typeName}
					</Box>
				</Box>
			) : (
				item.label
			);

			return (
				<TreeItem
					key={itemId}
					itemId={itemId}
					label={<Box title={item.declaration}>{label}</Box>}
					disabled={hasChildren ? false : item.disabled}
					sx={{
						...(isAddButton && {
							'& .MuiTreeItem-label': {
								color: 'primary.main',
								fontWeight: 'bold',
							},
						}),
						...(isNew && {
							'& .MuiTreeItem-label': {
								fontStyle: 'italic',
							},
						}),
						...(item.disabled &&
							hasChildren && {
								'& .MuiTreeItem-label': {
									opacity: 0.6,
									cursor: 'default',
								},
							}),
					}}
				>
					{hasChildren ? renderTreeItems(item.children as ObjectTreeViewItem[]) : null}
				</TreeItem>
			);
		});
	};

	return (
		<>
			<Box sx={{ mb: 1 }}>
				<FormControlLabel
					control={<Checkbox checked={allowAnyType} onChange={(e) => setAllowAnyType(e.target.checked)} size="small" />}
					label="Allow to select any type"
				/>
			</Box>
			<SimpleTreeView
				expandedItems={expandedItems}
				onExpandedItemsChange={handleExpandedItemsChange}
				selectedItems={currentSelectedItem}
				onSelectedItemsChange={(event, itemId) => handleSelectionChange(event, itemId as string | null)}
				sx={{
					minHeight: 200,
					maxHeight: 300,
					'& .MuiTreeItem-content': {
						'& .MuiTreeItem-iconContainer': {
							marginRight: 1,
							padding: '2px',
							borderRadius: '4px',
							'&:hover': {
								backgroundColor: 'action.hover',
							},
						},
						'& .MuiTreeItem-label': {
							cursor: 'pointer',
							padding: '2px 4px',
							borderRadius: '4px',
							'&:hover': {
								backgroundColor: 'action.hover',
							},
						},
					},
					...sx,
				}}
			>
				{renderTreeItems(items)}
			</SimpleTreeView>
		</>
	);
}
